/** @jsxImportSource @emotion/react */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Divider } from "primereact/divider";
import { Skeleton } from "primereact/skeleton";
import { Sidebar, TextoConFormato, Titulo, Boton, accessAPI, tituloBase } from "../../shared";

import { Card } from "primereact/card";

// "Operaciones": acciones que el organismo configura por tipo de certificado
// (AccionCliente → cliente.clasificaciones, que la API deduce de los procesos
// activos del cliente). Mismo contenido que el panel "Operaciones" de siempre:
// un texto introductorio y, por cada rubro con texto o link, su nombre, el
// texto con formato y un botón cuyo link puede ser interno ("/…") o una URL
// externa (nueva pestaña). Un rubro sin texto ni link existe solo para
// etiquetar al cliente en el admin y no se muestra.
const INTRO =
  "Desde esta sección podrá acceder a operaciones genéricas que tiene habilitadas para su usuario.";

// Las operaciones salen de las clasificaciones del cliente actual
export function useOperaciones() {
  const [cliente, setCliente] = useState(null);
  useEffect(() => {
    accessAPI("GET", "cliente/cliente", null, setCliente, () => {});
  }, []);
  // null mientras carga
  if (!cliente) return null;
  return (cliente.clasificaciones || []).filter((c) => c.texto || c.link);
}

function ListaOperaciones({ alNavegar }) {
  const operaciones = useOperaciones();
  const navigate = useNavigate();
  if (!operaciones) return <Skeleton height="4rem" />;
  // Sin acciones vinculadas a sus procesos activos, se dice en vez de dejar
  // el panel en blanco
  if (operaciones.length === 0)
    return (
      <div css={{ textAlign: "center", padding: "2em 1em", color: "var(--grisTextos)" }}>
        <i className="pi pi-bolt" css={{ fontSize: "1.5rem" }} />
        <div css={{ fontWeight: 600, color: "rgba(44, 44, 44, 1)", marginTop: "0.5em" }}>No tienes operaciones habilitadas por el momento</div>
        <p css={{ margin: "0.25em 0 0" }}>Aparecen según los procesos activos de tu empresa.</p>
      </div>
    );
  return operaciones.map((op) => {
    const esInterno = op.link?.startsWith("/");
    return (
      <div key={op.nombre}>
        <Divider />
        <h3 css={{ margin: "0 0 0.5rem", fontSize: "1.05rem", fontWeight: 600 }}>{op.nombre}</h3>
        {op.texto && (
          <div css={{ color: "var(--p-texto)", fontSize: "0.9rem" }}>
            <TextoConFormato texto={op.texto} />
          </div>
        )}
        {op.link && (
          <div css={{ marginTop: "0.75rem" }}>
            <Boton
              callback={() => {
                if (esInterno) {
                  alNavegar?.();
                  navigate(op.link);
                } else {
                  window.open(op.link, "_blank");
                }
              }}
            >
              {op.textoBoton || "Abrir"}
            </Boton>
          </div>
        )}
      </div>
    );
  });
}

// Panel lateral que abre el botón "Operaciones" del Inicio
export function PanelOperaciones({ visible, onHide }) {
  return (
    <Sidebar
      visible={visible}
      onHide={onHide}
      position="right"
      className="w-full md:w-30rem"
    >
      <h2 css={{ margin: "0 0 0.75rem", fontSize: "1.4rem", fontWeight: 600 }}>Operaciones</h2>
      <p css={{ margin: 0, color: "var(--p-texto-2)" }}>{INTRO}</p>
      <ListaOperaciones alNavegar={onHide} />
    </Sidebar>
  );
}

// Pantalla /operaciones
export default function Operaciones() {
  // Título de la pestaña, como en el admin
  useEffect(() => {
    document.title = "Operaciones";
    return () => (document.title = tituloBase());
  }, []);

  return (
    <>
      <Titulo
        urlVolver="/"
        subtitulo={INTRO}
      >
        Operaciones
      </Titulo>
      <Card css={{ marginBottom: "1em" }}>
        <ListaOperaciones />
      </Card>
    </>
  );
}
